import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DAYS_OF_WEEK, WeekdayNumber } from '~/utils/date-utils';
import { v4 as uuidV4, V4Options } from 'uuid';

import './daysOfWeek.scss';

const getDayOfWeek = (day: string) => {
    return DAYS_OF_WEEK[Number(day) as WeekdayNumber];
};

interface DaysOfWeekProps {
    'data-testid'?: string;
    /**
     * Whether the input field is editable
     */
    isEditable?: boolean;
    /**
     * The list of selected weekdays
     */
    selectedWeekdays: number[];
    /**
     * The size of the input field
     */
    size: 'small' | 'large';
    /**
     * The callback that receives the new values
     */
    onChange?: (values: number[]) => void;
}

const DaysOfWeek = ({
    onChange,
    selectedWeekdays,
    size = 'small',
    isEditable = false,
    ...extra
}: DaysOfWeekProps) => {
    const { t } = useTranslation('common');
    const handleOnChange = (day: number, checked: boolean) => {
        const newSelection = [...selectedWeekdays];
        if (!checked) {
            const index = newSelection.indexOf(day);
            newSelection.splice(index, 1);
        } else {
            newSelection.push(day);
        }
        onChange?.(newSelection);
    };

    const daysOfWeek = useMemo(() => {
        return Object.keys(DAYS_OF_WEEK).map((day) => {
            const uniqueId = uuidV4('dow' as V4Options);
            const isSelected = selectedWeekdays.includes(parseInt(day, 10));
            const isDisabled = selectedWeekdays.length === 1 && isSelected;
            const classes = classNames(
                'weekday-block',
                '_d-flex',
                '_ai-center',
                '_jc-center',
                {
                    inactive: !isSelected,
                    small: size === 'small',
                    large: size === 'large'
                }
            );
            if (isEditable) {
                return (
                    <span key={uniqueId}>
                        <label
                            htmlFor={uniqueId}
                            className="weekday-checkbox-label"
                            data-testid={
                                extra['data-testid']
                                    ? `${extra['data-testid']}-checkbox-label`
                                    : 'weekday-checkbox-label'
                            }
                        >
                            <span data-testid="weekday" className={classes}>
                                {t(`daysOfWeek.short.${getDayOfWeek(day)}`)}
                            </span>
                        </label>
                        <input
                            className="weekday-checkbox"
                            type="checkbox"
                            data-testid={
                                extra['data-testid']
                                    ? `${extra['data-testid']}-input-${day}`
                                    : `weekday-checkbox-input-${day}`
                            }
                            id={uniqueId}
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={(e) =>
                                handleOnChange(
                                    parseInt(day, 10),
                                    e.target.checked
                                )
                            }
                        />
                    </span>
                );
            }
            return (
                <span
                    data-testid={
                        extra['data-testid'] ? extra['data-testid'] : 'weekday'
                    }
                    className={classes}
                    key={uniqueId}
                >
                    {t(`daysOfWeek.short.${getDayOfWeek(day)}`)}
                </span>
            );
        });
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [t, selectedWeekdays]);

    return (
        <span
            className="weekday-wrapper _d-flex _ai-center"
            data-testid="daysofweek"
        >
            {daysOfWeek}
        </span>
    );
};

export default DaysOfWeek;
